import Phaser from 'phaser';
import { disableMenuScroll } from '../utils/menu-scroll.js';
import { sfx } from '../audio/SFX.js';
import { playContext as playMusic } from '../audio/Music.js';

// Same pattern as packages/client/src/payments/entry-fee.ts —
// VITE_SERVER_URL is JSON.stringified + trimmed at build time by
// vite.config.ts. Each Vercel project (devnet/mainnet) ships its own
// backend URL, no hardcoding.
const env = (import.meta as any).env;
const SERVER_URL = (env?.VITE_SERVER_URL || 'ws://localhost:2567').replace(/^ws/, 'http');

/**
 * Developer onboarding screen for Agent mode. Phaser scene that just
 * shows/hides the `#agent-info-overlay` HTML element. All visuals
 * (retro grid, hero CTA, CLI cards) live in CSS. The scene's only
 * jobs are: hide the canvas, wire the API/skill links to the right
 * server URL, and route the BACK button.
 */
export class AgentInfoScene extends Phaser.Scene {
  private overlay: HTMLElement | null = null;
  private backBtn: HTMLElement | null = null;
  private copyBtn: HTMLElement | null = null;
  private backHandler: (() => void) | null = null;
  private copyHandler: (() => void) | null = null;

  constructor() {
    super({ key: 'AgentInfoScene' });
  }

  create(): void {
    disableMenuScroll();
    playMusic(this.game, 'menu');
    this.cameras.main.setZoom(1);
    this.cameras.main.setScroll(0, 0);
    // Transparent canvas — the HTML overlay covers the screen
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');

    this.overlay = document.getElementById('agent-info-overlay');
    this.backBtn = document.getElementById('agent-back-btn');
    this.copyBtn = document.getElementById('agent-noinstall-copy');

    // Skill spec endpoint — used by all 3 CLI cards + the footer link + the no-install prompt
    const skillUrl = `${SERVER_URL}/api/agents/skill`;
    const docsUrl  = `${SERVER_URL}/api/game/info`;

    const skillIds = ['agent-cli-skill-1', 'agent-cli-skill-2', 'agent-cli-skill-3', 'agent-skill-link'];
    for (const id of skillIds) {
      const el = document.getElementById(id) as HTMLAnchorElement | null;
      if (el) el.href = skillUrl;
    }
    const docsLink = document.getElementById('agent-docs-link') as HTMLAnchorElement | null;
    if (docsLink) docsLink.href = docsUrl;

    // No-install prompt: substitute the placeholder URL with the real one
    const skillUrlSpan = document.getElementById('agent-noinstall-skill-url');
    if (skillUrlSpan) skillUrlSpan.textContent = skillUrl;

    this.overlay?.classList.add('visible');

    this.backHandler = () => {
      sfx(this, 'button_click');
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MainMenuScene');
      });
    };
    this.backBtn?.addEventListener('click', this.backHandler);

    this.copyHandler = () => {
      const promptText = `read ${skillUrl} and play 1 life balanced`;
      const finish = () => {
        sfx(this, 'button_click');
        this.copyBtn?.classList.add('copied');
        const btn = this.copyBtn as HTMLButtonElement | null;
        if (btn) {
          const original = btn.textContent ?? 'COPY';
          btn.textContent = 'COPIED';
          window.setTimeout(() => {
            btn.textContent = original;
            this.copyBtn?.classList.remove('copied');
          }, 1400);
        }
      };
      // navigator.clipboard fails in some embedded contexts → fall back to execCommand
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(promptText).then(finish).catch(() => finish());
      } else {
        const ta = document.createElement('textarea');
        ta.value = promptText;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch { /* ignore */ }
        document.body.removeChild(ta);
        finish();
      }
    };
    this.copyBtn?.addEventListener('click', this.copyHandler);

    this.events.once('shutdown', () => this.cleanup());
    this.events.once('destroy',  () => this.cleanup());
  }

  private cleanup(): void {
    if (this.backBtn && this.backHandler) {
      this.backBtn.removeEventListener('click', this.backHandler);
    }
    if (this.copyBtn && this.copyHandler) {
      this.copyBtn.removeEventListener('click', this.copyHandler);
    }
    this.backHandler = null;
    this.copyHandler = null;
    this.overlay?.classList.remove('visible');
  }
}
